<template>
  <main class="app-main">
    <div class="app-content-header">
      <div class="container-fluid">
        <div class="row">
          <div class="col-sm-6"><h3 class="mb-0">بيانات الشركة</h3></div>
          <div class="col-sm-6">
            <ol class="breadcrumb float-sm-end">
              <li class="breadcrumb-item"><router-link to="/">الرئيسية</router-link></li>
              <li class="breadcrumb-item">الإعدادات العامة</li>
              <li class="breadcrumb-item active">بيانات الشركة</li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <div class="app-content">
      <div class="container-fluid">
        <div class="card card-secondary card-outline mb-4">
          <div class="card-header"><div class="card-title">بيانات الشركة</div></div>
          <div class="card-body">
            <errorAlert v-if="hasError" :alertMessage="message" :errorNumber="errorNumber" />
            <successAlert v-if="hasSuccess" :message="message" />
            <mainLoader v-if="loading" />

            <template v-else>
              <ul class="nav nav-tabs mb-4" role="tablist" aria-label="أقسام بيانات الشركة">
                <li class="nav-item" role="presentation">
                  <button type="button" class="nav-link" :class="{ active: activeTab === 'company' }"
                    role="tab" :aria-selected="activeTab === 'company'" @click="switchTab('company')">بيانات الشركة</button>
                </li>
                <li class="nav-item" role="presentation">
                  <button type="button" class="nav-link" :class="{ active: activeTab === 'branches' }"
                    role="tab" :aria-selected="activeTab === 'branches'" @click="switchTab('branches')">الفروع</button>
                </li>
                <li class="nav-item" role="presentation">
                  <button type="button" class="nav-link" :class="{ active: activeTab === 'banks' }"
                    role="tab" :aria-selected="activeTab === 'banks'" @click="switchTab('banks')">الحسابات البنكية</button>
                </li>
              </ul>

              <section v-if="activeTab === 'company'" role="tabpanel">
                <form @submit.prevent="saveCompany">
                  <div class="row">
                    <div class="col-12 col-md-6 mb-3">
                      <label class="form-label" for="company-name">اسم الشركة</label>
                      <input id="company-name" v-model.trim="details.companyName" class="form-control" type="text"
                        maxlength="255" required :disabled="!canEdit" />
                    </div>
                    <div class="col-12 col-md-6 mb-3">
                      <label class="form-label" for="company-logo">شعار الشركة</label>
                      <div v-if="logoSrc" class="mb-2">
                        <img :src="logoSrc" alt="شعار الشركة" class="img-thumbnail" style="max-width: 180px; max-height: 120px" />
                      </div>
                      <input v-if="canEdit" id="company-logo" class="form-control" type="file"
                        accept="image/png,image/jpeg,image/webp" @change="selectLogo" />
                      <button v-if="canEdit && logoSrc" type="button" class="btn btn-outline-danger btn-sm mt-2"
                        @click="clearLogo">إزالة الشعار</button>
                    </div>
                    <div class="col-12 col-md-6 mb-3">
                      <label class="form-label" for="tax-register">السجل الضريبي</label>
                      <input id="tax-register" v-model.trim="details.taxRegister" class="form-control" type="text"
                        maxlength="255" :disabled="!canEdit" />
                    </div>
                    <div class="col-12 col-md-6 mb-3">
                      <label class="form-label" for="commercial-register">رقم السجل التجاري</label>
                      <input id="commercial-register" v-model.trim="details.commercialRegister" class="form-control"
                        type="text" maxlength="255" :disabled="!canEdit" />
                    </div>
                    <div class="col-12 col-md-6 mb-3">
                      <label class="form-label" for="tax-card-number">رقم البطاقة الضريبية</label>
                      <input id="tax-card-number" v-model.trim="details.taxCardNumber" class="form-control" type="text"
                        maxlength="255" :disabled="!canEdit" />
                    </div>
                    <div class="col-12 col-md-6 mb-3">
                      <label class="form-label" for="company-email">البريد الإلكتروني</label>
                      <input id="company-email" v-model.trim="details.email" class="form-control" type="email"
                        maxlength="255" dir="ltr" :disabled="!canEdit" />
                    </div>
                    <div class="col-12 col-md-6 mb-3">
                      <label class="form-label" for="company-website">الموقع الإلكتروني</label>
                      <input id="company-website" v-model.trim="details.website" class="form-control" type="url"
                        maxlength="255" dir="ltr" placeholder="https://" :disabled="!canEdit" />
                    </div>
                    <div class="col-12 col-md-6 mb-3">
                      <label class="form-label" for="company-telephone">رقم الهاتف</label>
                      <input id="company-telephone" v-model.trim="details.telephoneNumber" class="form-control" type="tel"
                        maxlength="255" dir="ltr" :disabled="!canEdit" />
                    </div>
                  </div>
                  <div v-if="canEdit" class="text-end">
                    <button class="btn btn-success" type="submit" :disabled="saving">حفظ بيانات الشركة</button>
                  </div>
                </form>
              </section>

              <section v-else-if="activeTab === 'branches'" role="tabpanel">
                <form @submit.prevent="saveBranches">
                  <div v-if="!details.branches.length" class="alert alert-info">لا توجد فروع مضافة</div>
                  <div v-for="(branch, index) in details.branches" :key="branch._key" class="row align-items-end mb-3">
                    <div class="col-12 col-md-8">
                      <label class="form-label" :for="`branch-${branch._key}`">اسم الفرع {{ index + 1 }}</label>
                      <input :id="`branch-${branch._key}`" v-model.trim="branch.name" class="form-control"
                        type="text" maxlength="255" required :disabled="!canEdit" />
                    </div>
                    <div v-if="canEdit" class="col-12 col-md-4 mt-2 mt-md-0">
                      <button type="button" class="btn btn-outline-danger" @click="details.branches.splice(index, 1)">حذف الفرع</button>
                    </div>
                  </div>
                  <div v-if="canEdit" class="d-flex justify-content-between gap-2 mt-4">
                    <button type="button" class="btn btn-outline-primary" @click="addBranch">إضافة فرع</button>
                    <button type="submit" class="btn btn-success" :disabled="saving">حفظ الفروع</button>
                  </div>
                </form>
              </section>

              <section v-else role="tabpanel">
                <form @submit.prevent="saveBankAccounts">
                  <div v-if="!details.bankAccounts.length" class="alert alert-info">لا توجد حسابات بنكية مضافة</div>
                  <div v-for="(account, index) in details.bankAccounts" :key="account._key" class="border rounded p-3 mb-3">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <strong>{{ account.label || account.bankName || account.accountName || 'حساب بنكي' }}</strong>
                      <button v-if="canEdit" type="button" class="btn btn-outline-danger btn-sm"
                        @click="details.bankAccounts.splice(index, 1)">حذف الحساب</button>
                    </div>
                    <div class="row">
                      <div class="col-12 col-lg-6 mb-3">
                        <label class="form-label" :for="`account-label-${account._key}`">تسمية الحساب</label>
                        <input :id="`account-label-${account._key}`" v-model.trim="account.label" class="form-control"
                          type="text" maxlength="255" placeholder="مثال: الحساب الرئيسي" :required="!account.id" :disabled="!canEdit" />
                      </div>
                      <div class="col-12 col-lg-6 mb-3">
                        <label class="form-label" :for="`account-number-${account._key}`">رقم الحساب</label>
                        <input :id="`account-number-${account._key}`" v-model.trim="account.accountNumber" class="form-control"
                          type="text" maxlength="100" dir="ltr" :required="!account.id" :disabled="!canEdit" />
                      </div>
                      <div class="col-12 col-lg-4 mb-3">
                        <label class="form-label" :for="`iban-${account._key}`">رقم الآيبان IBAN</label>
                        <input :id="`iban-${account._key}`" v-model.trim="account.iban" class="form-control"
                          type="text" maxlength="34" dir="ltr" required :disabled="!canEdit" />
                      </div>
                      <div class="col-12 col-lg-4 mb-3">
                        <label class="form-label" :for="`bank-name-${account._key}`">اسم البنك</label>
                        <input :id="`bank-name-${account._key}`" v-model.trim="account.bankName" class="form-control"
                          type="text" maxlength="255" required :disabled="!canEdit" />
                      </div>
                      <div class="col-12 col-lg-4 mb-3">
                        <label class="form-label" :for="`account-name-${account._key}`">اسم الحساب</label>
                        <input :id="`account-name-${account._key}`" v-model.trim="account.accountName" class="form-control"
                          type="text" maxlength="255" required :disabled="!canEdit" />
                      </div>
                    </div>
                  </div>
                  <div v-if="canEdit" class="d-flex justify-content-between gap-2 mt-4">
                    <button type="button" class="btn btn-outline-primary" @click="addBankAccount">إضافة حساب بنكي</button>
                    <button type="submit" class="btn btn-success" :disabled="saving">حفظ الحسابات البنكية</button>
                  </div>
                </form>
              </section>
            </template>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { checkAccess } from '@/helpers/tools';

const emptyDetails = () => ({
  companyName: '', logo: null, taxRegister: '', commercialRegister: '',
  taxCardNumber: '', email: '', website: '', telephoneNumber: '',
  branches: [], bankAccounts: [],
});

export default {
  name: 'CompanyDetails',
  data() {
    return {
      activeTab: 'company',
      details: emptyDetails(),
      loading: true,
      saving: false,
      hasError: false,
      hasSuccess: false,
      message: '',
      errorNumber: 0,
      logoFile: null,
      logoPreview: null,
      removeLogo: false,
      nextKey: 0,
    };
  },
  computed: {
    canEdit() { return checkAccess('settings_update'); },
    logoSrc() {
      if (this.logoPreview) return this.logoPreview;
      return this.details.logo && !this.removeLogo
        ? `${this.$store.getters.MEDIA_URL}${this.details.logo}` : null;
    },
  },
  methods: {
    switchTab(tab) {
      this.activeTab = tab;
      this.hasError = false;
      this.hasSuccess = false;
    },
    keyed(items) {
      return (items || []).map(item => ({ ...item, _key: ++this.nextKey }));
    },
    keyedBankAccounts(items) {
      return this.keyed((items || []).map(account => ({
        ...account,
        label: account.label || '',
        accountNumber: account.accountNumber || '',
      })));
    },
    fail(message) {
      this.message = message;
      this.errorNumber = 0;
      this.hasError = true;
      this.hasSuccess = false;
    },
    requestError(error) {
      [this.message, this.errorNumber] = this.$globalError(error, true);
      this.hasError = true;
      this.hasSuccess = false;
    },
    success() {
      this.message = 'تم الحفظ بنجاح';
      this.hasSuccess = true;
      this.hasError = false;
    },
    async load() {
      try {
        const { data } = await this.$http.get('/api/admin/companyDetails');
        this.details = {
          ...emptyDetails(), ...data.data,
          branches: this.keyed(data.data.branches),
          bankAccounts: this.keyedBankAccounts(data.data.bankAccounts),
        };
      } catch (error) {
        this.requestError(error);
      } finally {
        this.loading = false;
      }
    },
    selectLogo(event) {
      const file = event.target.files[0];
      if (!file) return;
      if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 5 * 1024 * 1024) {
        event.target.value = '';
        this.fail('الشعار يجب أن يكون صورة PNG أو JPG أو WebP بحجم لا يتجاوز 5 ميجابايت');
        return;
      }
      if (this.logoPreview) URL.revokeObjectURL(this.logoPreview);
      this.logoFile = file;
      this.logoPreview = URL.createObjectURL(file);
      this.removeLogo = false;
      this.hasError = false;
    },
    clearLogo() {
      if (this.logoPreview) URL.revokeObjectURL(this.logoPreview);
      this.logoPreview = null;
      this.logoFile = null;
      this.removeLogo = true;
      const input = document.getElementById('company-logo');
      if (input) input.value = '';
    },
    async saveCompany() {
      if (!this.canEdit || this.saving) return;
      if (!this.details.companyName.trim()) return this.fail('اسم الشركة مطلوب');
      const payload = new FormData();
      for (const field of ['companyName', 'taxRegister', 'commercialRegister', 'taxCardNumber',
        'email', 'website', 'telephoneNumber']) {
        payload.append(field, this.details[field] || '');
      }
      if (this.logoFile) payload.append('logo', this.logoFile);
      if (this.removeLogo) payload.append('removeLogo', 'true');
      this.saving = true;
      try {
        const { data } = await this.$http.put('/api/admin/companyDetails/company', payload);
        Object.assign(this.details, {
          companyName: data.data.companyName, logo: data.data.logo,
          taxRegister: data.data.taxRegister, commercialRegister: data.data.commercialRegister,
          taxCardNumber: data.data.taxCardNumber, email: data.data.email,
          website: data.data.website, telephoneNumber: data.data.telephoneNumber,
        });
        if (this.logoPreview) URL.revokeObjectURL(this.logoPreview);
        this.logoPreview = null;
        this.logoFile = null;
        this.removeLogo = false;
        const input = document.getElementById('company-logo');
        if (input) input.value = '';
        this.success();
      } catch (error) {
        this.requestError(error);
      } finally {
        this.saving = false;
      }
    },
    addBranch() {
      if (this.details.branches.length < 100) this.details.branches.push({ _key: ++this.nextKey, name: '' });
    },
    async saveBranches() {
      if (!this.canEdit || this.saving) return;
      const branches = this.details.branches.map(({ id, name }) => ({ id, name: name.trim() }));
      if (branches.some(branch => !branch.name)) return this.fail('اسم الفرع مطلوب');
      if (new Set(branches.map(branch => branch.name.toLocaleLowerCase())).size !== branches.length)
        return this.fail('أسماء الفروع يجب أن تكون مختلفة');
      this.saving = true;
      try {
        const { data } = await this.$http.put('/api/admin/companyDetails/branches', { branches });
        this.details.branches = this.keyed(data.data.branches);
        this.success();
      } catch (error) {
        this.requestError(error);
      } finally {
        this.saving = false;
      }
    },
    addBankAccount() {
      if (this.details.bankAccounts.length < 100) {
        this.details.bankAccounts.push({
          _key: ++this.nextKey, label: '', accountNumber: '',
          iban: '', bankName: '', accountName: '',
        });
      }
    },
    async saveBankAccounts() {
      if (!this.canEdit || this.saving) return;
      const bankAccounts = this.details.bankAccounts.map(({ id, label, accountNumber, iban, bankName, accountName }) => ({
        id, label: (label || '').trim(), accountNumber: (accountNumber || '').trim(),
        iban: iban.replace(/\s/g, '').toUpperCase(),
        bankName: bankName.trim(), accountName: accountName.trim(),
      }));
      if (bankAccounts.some(account => !/^[A-Z]{2}[A-Z0-9]{13,32}$/.test(account.iban)))
        return this.fail('رقم الآيبان IBAN غير صحيح');
      if (bankAccounts.some(account => !account.bankName || !account.accountName))
        return this.fail('اسم البنك واسم الحساب مطلوبان');
      if (bankAccounts.some(account => !account.id && (!account.label || !account.accountNumber)))
        return this.fail('تسمية الحساب ورقم الحساب مطلوبان للحسابات الجديدة');
      if (new Set(bankAccounts.map(account => account.iban)).size !== bankAccounts.length)
        return this.fail('أرقام الحسابات البنكية يجب أن تكون مختلفة');
      this.saving = true;
      try {
        const { data } = await this.$http.put('/api/admin/companyDetails/bankAccounts', { bankAccounts });
        this.details.bankAccounts = this.keyedBankAccounts(data.data.bankAccounts);
        this.success();
      } catch (error) {
        this.requestError(error);
      } finally {
        this.saving = false;
      }
    },
  },
  created() { this.load(); },
  beforeUnmount() {
    if (this.logoPreview) URL.revokeObjectURL(this.logoPreview);
  },
};
</script>
